/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import {
    CluLinkedModelPredictionSwagger,
    CluTestConversationModelSwagger,
    CluTestOrchestrationModelSwagger,
    LuisLinkedModelPredictionSwagger,
    PredictedLinkedIntentSwagger,
    PredictedQnALinkedIntentSwagger
} from 'src/clu/core/services/test/cluTestModelSwagger';
import { CluTestModelResults } from 'src/clu/core/services/test/types';
import { EntityInstanceValueSwagger } from 'src/luis/projects/testPane/types/swagger/EndpointPredictionSwagger';

const cluLinkedTestModelToTestJobInfo = (
    swagger: PredictedLinkedIntentSwagger<CluLinkedModelPredictionSwagger>
): Pick<CluTestModelResults, 'entities' | 'topLinkedIntent'> => {
    const topIntentName = swagger.result.prediction.topIntent;
    const topIntent = swagger.result.prediction.intents.find(intent => intent.category === topIntentName);

    return {
        entities: swagger.result.prediction.entities,
        topLinkedIntent: { confidenceScore: topIntent.confidenceScore, name: topIntent.category }
    };
};

const luisLinkedTestModelToTestJobInfo = (
    swagger: PredictedLinkedIntentSwagger<LuisLinkedModelPredictionSwagger>
): Pick<CluTestModelResults, 'entities' | 'topLinkedIntent'> => {
    const topIntentName = swagger.result.prediction.topIntent;
    const topIntent = swagger.result.prediction.intents[topIntentName];

    const predictedEntities: CluTestModelResults['entities'] = swagger.result.prediction.entities.$instance
        ? Object.keys(swagger.result.prediction.entities.$instance).reduce((output, name) => {
              const entityInstances: EntityInstanceValueSwagger[] = swagger.result.prediction.entities.$instance[name];
              const utteranceEntities: CluTestModelResults['entities'] = entityInstances.map(instance => ({
                  category: name,
                  text: instance.text,
                  length: instance.length,
                  offset: instance.startIndex,
                  confidenceScore: instance.score
              }));

              return [...output, ...utteranceEntities];
          }, [])
        : [];

    return {
        entities: predictedEntities,
        topLinkedIntent: { confidenceScore: topIntent.score, name: topIntentName }
    };
};

const qnaLinkedTestModelToTestJobInfo = (swagger: PredictedQnALinkedIntentSwagger): Pick<CluTestModelResults, 'answer'> => {
    const answer = swagger.result.answers?.sort((a, b) => (a.confidenceScore < b.confidenceScore ? 1 : -1))?.[0];

    return { answer };
};

export const cluConversationTestModelToTestJobInfo = (swagger: CluTestConversationModelSwagger): CluTestModelResults => {
    const topIntentName = swagger.prediction.topIntent;
    const topIntent = swagger.prediction.intents.find(intent => intent.category === topIntentName);

    return {
        entities: swagger.prediction.entities,
        topIntent: { confidenceScore: topIntent.confidenceScore, name: topIntent.category },
        originalJsonResult: swagger
    };
};

export const cluOrchestrationTestModelToTestJobInfo = (swagger: CluTestOrchestrationModelSwagger): CluTestModelResults => {
    const topIntentName = swagger.prediction.topIntent;
    const topIntent = swagger.prediction.intents[topIntentName];
    let testJobResult: CluTestModelResults = { originalJsonResult: swagger, topIntent: null };
    const testJobTopIntent = { confidenceScore: topIntent.confidenceScore, name: topIntentName };

    switch (topIntent.targetProjectKind) {
        case 'Conversation':
            testJobResult = {
                ...testJobResult,
                topIntent: { ...testJobTopIntent, orchestrationType: 'Conversation' },
                ...cluLinkedTestModelToTestJobInfo(<PredictedLinkedIntentSwagger<CluLinkedModelPredictionSwagger>>topIntent)
            };
            break;
        case 'Luis':
            testJobResult = {
                ...testJobResult,
                topIntent: { ...testJobTopIntent, orchestrationType: 'Luis' },
                ...luisLinkedTestModelToTestJobInfo(<PredictedLinkedIntentSwagger<LuisLinkedModelPredictionSwagger>>topIntent)
            };
            break;
        case 'QuestionAnswering':
            testJobResult = {
                ...testJobResult,
                topIntent: { ...testJobTopIntent, orchestrationType: 'QuestionAnswering' },
                ...qnaLinkedTestModelToTestJobInfo(<PredictedQnALinkedIntentSwagger>topIntent)
            };
            break;
        case 'NonLinked':
        default:
            testJobResult = { ...testJobResult, topIntent: { ...testJobTopIntent } };
    }

    return testJobResult;
};
